import {  AfterViewInit, Component, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { ScaleType } from '@swimlane/ngx-charts';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { Customer, IPieChartItem } from 'src/app/app.types';
import { UsageAmountsService } from 'src/app/services/usage/usage-amounts.service';
import { convertToUtcDate } from 'src/util/date-helpers';

@Component({
    selector: 'upload-statuses-chart',
    templateUrl: './upload-statuses-chart.component.html',
    styleUrls: ['./upload-statuses-chart.component.scss'],
    standalone: false
})
export class UploadStatusesChartComponent implements OnInit {
    @Input() showLegend = false;

    @Input()
    public set customer(v : Customer) {
      this.tenantIdChanged$.next(v?.tenantId);
      this.earliestSelectionDate = v?.subscriptionStartDate ? new Date(v.subscriptionStartDate) : this.earliestSelectionDate;
    }

    public set startDate(v : Date) {
      this._startDate = v;
      this.startDateChanged$.next(v);
    }

    public get startDate() : Date {
      return this._startDate;
    }
    
    @Input() uploadsChartMin = 425;
    @Input() uploadsChartMax = 720;
    chartSize: [number, number] = [this.uploadsChartMin, this.uploadsChartMin * 0.55];

    // [top, right, bottom, left] — L/R kept equal so the ring stays dead-centre; the wider
    // L/R (vs ngx-charts' default 80) reserves room for long labels so they don't overhang.
    chartMargins = [0, 150, 0, 150];

    _startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    earliestSelectionDate: Date = new Date(2024, 0, 1);
    latestSelectionDate: Date = new Date();

    tenantIdChanged$ = new Subject<string>();
    startDateChanged$ = new BehaviorSubject<Date>(this._startDate);

    @Input() pieData: IPieChartItem[] = [];

    customColorsSchema = {
        name: "customScheme",
        selectable: true,
        group: ScaleType.Ordinal,
        domain: [],
    };

    total: number;

    isShowingCurrentMonth?: boolean;

    constructor(private usageAmountsService: UsageAmountsService, private hostElement: ElementRef) { 
    }

    ngOnInit(): void {
      this.setChartDimensions();
      combineLatest([this.tenantIdChanged$, this.startDateChanged$]).pipe(
        filter(([tenantId, startDate]) => !!tenantId && !!startDate),
        switchMap(([tenantId, startDate]) => this.usageAmountsService.getExportStats(tenantId, 
          convertToUtcDate(startDate).toISOString(), convertToUtcDate(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1)).toISOString()))
      ).subscribe(data => {
        this.pieData = [
          { name: `Successful (${data.successfulCount})`, value: data.successfulCount, color: "#3aae58" },
          { name: `Deleted (${data.deletedCount})`, value: data.deletedCount, color: "#f6665e" },
          { name: `Cancelled (${data.cancelledCount})`, value: data.cancelledCount, color: "#ababab" },
          { name: `Partially Complete (${data.partiallyCompleteCount})`, value: data.partiallyCompleteCount, color: "#ff7928" },
          { name: `Partially Retrieved (${data.partiallyRetrievedCount})`, value: data.partiallyRetrievedCount, color: "#dbbb32" },
          { name: `Failed (${data.failedCount})`, value: data.failedCount, color: "#ca1831" },
        ];
        this.customColorsSchema.domain = this.pieData.map(d => d.color);
        this.total = data.successfulCount + data.deletedCount + data.cancelledCount + data.partiallyCompleteCount + data.partiallyRetrievedCount + data.failedCount;
        this.isShowingCurrentMonth = new Date().getMonth() === this.startDate.getMonth() && new Date().getFullYear() === this.startDate.getFullYear();
      });
    }

    @HostListener("window:resize")
    onResize() {
        this.setChartDimensions();
    }

    setChartDimensions() {
      if(this.hostElement.nativeElement.offsetWidth > this.uploadsChartMax){
        this.chartSize = [this.uploadsChartMax, this.uploadsChartMax * 0.55];
      } else {
        this.chartSize = [this.hostElement.nativeElement.offsetWidth, this.hostElement.nativeElement.offsetWidth * 0.55];
      }
    }
}
